import { where, Timestamp, orderBy, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { FirestoreService, type AuditContext } from '@/lib/firestore';
import { db } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import { Logger } from '@/lib/logger';
import { activityEventService } from '@/shared/services/activityEventService';
import type {
  ComplexityLevel,
  TeamExperience,
  ProjectStyle,
  DecisionDriver,
  ApprovalStatus,
  ConversationEntry,
  ConversationMessageType,
  ApprovalHistoryEntry,
  CreateWorkflowSpotData,
  UpdateWorkflowSpotData,
  AddConversationEntryData
} from '../types/enhancedWorkflow';
import {
  CreateWorkflowSpotDataSchema,
  UpdateWorkflowSpotDataSchema,
  AddConversationEntryDataSchema
} from '../types/enhancedWorkflow';

const logger = Logger.createScoped('WorkflowSpotService');

// ============================================================================
// WORKFLOW SPOT DATA MODEL
// ============================================================================

/**
 * Clean WorkflowSpot interface for the new workflow_spots collection
 */
export interface WorkflowSpot {
  id: string;
  
  // Basic workflow spot info
  workflow_name: string;
  team_id: string;
  team_name: string;
  program_id: string;
  program_name: string;
  
  // Configuration
  complexity: ComplexityLevel;
  team_experience: TeamExperience[];
  project_style_preference: ProjectStyle[];
  confidence_level: number; // 1-10
  confidence_history?: { previous_value: number; changed_at: Timestamp; changed_by: string }[];
  decision_drivers: DecisionDriver[];
  
  // Role assignments
  first_responder: string | null;
  production_support_1: string | null;
  production_support_2: string | null;
  strategy_pfm_support: string | null;
  ideal_start_date: string | null;
  
  // Asana integration
  asana_task_gid: string | null;
  
  // Approval workflow
  approval_status: ApprovalStatus;
  is_archived: boolean;
  
  // Earmarked status for sales resourcing
  earmarked_status: boolean;
  earmarked_text: string | null;
  
  approval_metadata: {
    submitted_at?: Timestamp;
    submitted_by?: string;
    submitted_by_name?: string;
    reviewed_at?: Timestamp;
    reviewed_by?: string;
    reviewed_by_name?: string;
    review_notes?: string;
    approval_history: ApprovalHistoryEntry[];
  };
  
  // Conversation system
  notes: {
    content: string;
    conversation_history: ConversationEntry[];
    last_updated: Timestamp;
    last_updated_by: string;
    last_updated_by_name: string;
    
    // Prompt questions for initial setup
    team_strengths?: string | null;
    growth_opportunities?: string | null;
    current_projects?: string | null;
    matching_considerations?: string | null;
  };
  
  // Standard audit fields
  created_at: Timestamp;
  updated_at: Timestamp;
  created_by: string;
  created_by_name: string;
  last_modified_by: string;
  last_modified_by_name: string;
  version: number;
  
  // Metadata
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

// ============================================================================
// WORKFLOW SPOT SERVICE
// ============================================================================

/**
 * WorkflowSpotService - Clean, focused service for managing workflow spots
 * 
 * Features:
 * - CRUD operations for workflow spots only
 * - Approval workflow management (submit, approve, defer)
 * - Conversation management
 * - Strong typing throughout (no any types)
 * - Uses existing FirestoreService pattern
 * - Comprehensive error handling and logging
 */
class WorkflowSpotService {
  private firestoreService: FirestoreService<WorkflowSpot>;

  constructor() {
    this.firestoreService = new FirestoreService<WorkflowSpot>(COLLECTION_NAMES.WORKFLOW_SPOTS, {
      enableAudit: true,
    });
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new workflow spot
   */
  async createWorkflowSpot(
    data: CreateWorkflowSpotData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<string> {
    try {
      logger.info('Creating workflow spot', { workflow_name: data.workflow_name, team_id: data.team_id });

      // Validate input data
      const validatedData = CreateWorkflowSpotDataSchema.parse(data);
      
      const now = Timestamp.now();
      
      // Generate conversation entry ID
      const conversationEntryId = `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Create initial conversation entry
      const initialConversationEntry: ConversationEntry = {
        id: conversationEntryId,
        author_id: userId,
        author_name: userName,
        message: 'Workflow spot created',
        timestamp: now,
        message_type: 'status_change' as ConversationMessageType,
        metadata: {
          new_status: 'pending_approval' as ApprovalStatus,
        },
      };

      const workflowSpot: Omit<WorkflowSpot, 'id'> = {
        // Basic info
        workflow_name: validatedData.workflow_name,
        team_id: validatedData.team_id,
        team_name: validatedData.team_name,
        program_id: validatedData.program_id,
        program_name: validatedData.program_name,
        
        // Configuration
        complexity: validatedData.complexity,
        team_experience: validatedData.team_experience,
        project_style_preference: validatedData.project_style_preference,
        confidence_level: validatedData.confidence_level,
        decision_drivers: validatedData.decision_drivers,
        
        // Role assignments
        first_responder: validatedData.first_responder || null,
        production_support_1: validatedData.production_support_1 || null,
        production_support_2: validatedData.production_support_2 || null,
        strategy_pfm_support: validatedData.strategy_pfm_support || null,
        ideal_start_date: validatedData.ideal_start_date || null,
        
        // Asana integration
        asana_task_gid: null,
        
        // Approval workflow
        approval_status: 'pending_approval',
        is_archived: false,
        
        // Earmarked status (defaults to false)
        earmarked_status: false,
        earmarked_text: null,
        
        approval_metadata: {
          approval_history: [],
        },
        
        // Conversation system
        notes: {
          content: validatedData.notes_content,
          conversation_history: [initialConversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
          // Convert undefined to null for Firestore compatibility
          team_strengths: validatedData.team_strengths || null,
          growth_opportunities: validatedData.growth_opportunities || null,
          current_projects: validatedData.current_projects || null,
          matching_considerations: validatedData.matching_considerations || null,
        },
        
        // Audit fields
        created_at: now,
        updated_at: now,
        created_by: userId,
        created_by_name: userName,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: 1,
        
        // Metadata - only include if provided to avoid Firestore undefined errors
        ...(validatedData.metadata && { metadata: validatedData.metadata }),
      };

      const spotId = await this.firestoreService.create(workflowSpot, {
        userId,
        userName,
        action: 'create',
        resourceType: 'workflow_spot',
        resourceId: '', // Will be filled by the service
        cycleId: auditContext.cycleId,
      });
      
      // Emit activity event for workflow spot creation
      try {
        // Build fields_changed array for key metadata
        const fieldsChanged = [
          {
            field_name: 'complexity',
            old_value: null,
            new_value: validatedData.complexity,
            display_name: 'Complexity Level',
          },
          {
            field_name: 'confidence_level',
            old_value: null,
            new_value: validatedData.confidence_level,
            display_name: 'Confidence Level',
          },
          {
            field_name: 'team_experience',
            old_value: null,
            new_value: validatedData.team_experience.join(', '),
            display_name: 'Team Experience',
          },
          {
            field_name: 'project_style_preference',
            old_value: null,
            new_value: validatedData.project_style_preference.join(', '),
            display_name: 'Project Style',
          },
          {
            field_name: 'decision_drivers',
            old_value: null,
            new_value: validatedData.decision_drivers.join(', '),
            display_name: 'Decision Drivers',
          },
        ];

        // Add ideal_start_date if provided
        if (validatedData.ideal_start_date) {
          fieldsChanged.push({
            field_name: 'ideal_start_date',
            old_value: null,
            new_value: validatedData.ideal_start_date,
            display_name: 'Ideal Start Date',
          });
        }

        // Add context fields if provided
        if (validatedData.team_strengths) {
          fieldsChanged.push({
            field_name: 'team_strengths',
            old_value: null,
            new_value: validatedData.team_strengths,
            display_name: 'Team Strengths',
          });
        }
        if (validatedData.growth_opportunities) {
          fieldsChanged.push({
            field_name: 'growth_opportunities',
            old_value: null,
            new_value: validatedData.growth_opportunities,
            display_name: 'Growth Opportunities',
          });
        }
        if (validatedData.current_projects) {
          fieldsChanged.push({
            field_name: 'current_projects',
            old_value: null,
            new_value: validatedData.current_projects,
            display_name: 'Current Projects',
          });
        }
        if (validatedData.matching_considerations) {
          fieldsChanged.push({
            field_name: 'matching_considerations',
            old_value: null,
            new_value: validatedData.matching_considerations,
            display_name: 'Matching Considerations',
          });
        }

        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'workflow_spot.created',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'workflow_spot',
            resource_id: spotId,
            resource_name: validatedData.workflow_name,
          },
          message: `Created workflow spot for ${validatedData.team_name || 'team'}`,
          context: {
            program_id: validatedData.program_id,
            program_name: validatedData.program_name,
            team_id: validatedData.team_id,
            team_name: validatedData.team_name,
          },
          change: {
            action: 'created',
            // Record key metadata as field changes for the activity feed
            fields_changed: fieldsChanged,
            // Also store the full initial state for reference (filter out undefined and null values for Firestore)
            new_state: Object.fromEntries(
              Object.entries({
                complexity: validatedData.complexity,
                team_experience: validatedData.team_experience,
                project_style_preference: validatedData.project_style_preference,
                confidence_level: validatedData.confidence_level,
                decision_drivers: validatedData.decision_drivers,
                first_responder: validatedData.first_responder,
                production_support_1: validatedData.production_support_1,
                production_support_2: validatedData.production_support_2,
                strategy_pfm_support: validatedData.strategy_pfm_support,
                ideal_start_date: validatedData.ideal_start_date,
                notes_content: validatedData.notes_content,
                team_strengths: validatedData.team_strengths,
                growth_opportunities: validatedData.growth_opportunities,
                current_projects: validatedData.current_projects,
                matching_considerations: validatedData.matching_considerations,
              }).filter(([_, value]) => value !== undefined && value !== null)
            ),
          },
          metadata: {
            affected_user_ids: [userId],
            affected_workflow_ids: [],
            affected_employee_ids: [],
            tags: [
              'workflow',
              'workflow_spot',
              'created',
              'capacity',
              validatedData.complexity,
              ...validatedData.team_experience,
              ...validatedData.project_style_preference,
            ],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit workflow_spot.created event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
      
      return spotId;

    } catch (error) {
      logger.error('Failed to create workflow spot', error, { workflow_name: data.workflow_name });
      throw error;
    }
  }

  /**
   * Get a workflow spot by ID
   */
  async getWorkflowSpot(spotId: string): Promise<WorkflowSpot | null> {
    try {
      
      const spot = await this.firestoreService.get(spotId);
      
      if (spot) {
      } else {
      }
      
      return spot;
    } catch (error) {
      logger.error('Failed to get workflow spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Get workflow spots by team ID
   */
  async getWorkflowSpotsByTeam(teamId: string): Promise<WorkflowSpot[]> {
    try {
      
      const spots = await this.firestoreService.list([
        where('team_id', '==', teamId),
        orderBy('created_at', 'desc')
      ]);
      
      return spots;
    } catch (error) {
      console.error(`❌ [WorkflowSpotService] Error getting workflow spots for team "${teamId}":`, error);
      logger.error('Failed to get workflow spots by team', error, { teamId });
      throw error;
    }
  }

  /**
   * Get workflow spots by program ID
   */
  async getWorkflowSpotsByProgram(programId: string): Promise<WorkflowSpot[]> {
    try {
      
      const spots = await this.firestoreService.list([
        where('program_id', '==', programId),
        orderBy('created_at', 'desc')
      ]);
      
      return spots;
    } catch (error) {
      console.error(`❌ [WorkflowSpotService] Error getting workflow spots for program "${programId}":`, error);
      logger.error('Failed to get workflow spots by program', error, { programId });
      throw error;
    }
  }

  /**
   * Get workflow spots filtered for Summary page display
   * Business rules enforced at service layer:
   * - confidence_level > 5 (strictly greater than)
   * - is_archived === false (not archived)
   * - approval_status === 'approved' (approved spots only)
   */
  async getWorkflowSpotsForSummary(programId: string): Promise<WorkflowSpot[]> {
    try {
      
      // Get all spots for the program first
      const allSpots = await this.getWorkflowSpotsForSummaryRaw(programId);
      
      // Filter spots based on Summary page business rules:
      // - confidence_level > 5 (not >= 5, must be strictly greater)
      // - is_archived === false
      // - approval_status === 'approved'
      const filteredSpots = allSpots.filter(spot => {
        const passesConfidence = spot.confidence_level > 5;
        const isNotArchived = spot.is_archived === false;
        const isApproved = spot.approval_status === 'approved';
        return passesConfidence && isNotArchived && isApproved;
      });
      
      return filteredSpots;
    } catch (error) {
      logger.error('Failed to get workflow spots for summary', error, { programId });
      throw error;
    }
  }

  /**
   * Get workflow spots for a program without business rule filtering
   * Returns all spots regardless of confidence level, archived status, or approval status
   * Use this when you need raw data for analysis or administrative purposes
   */
  async getWorkflowSpotsForSummaryRaw(programId: string): Promise<WorkflowSpot[]> {
    try {
      // Returns all spots without filtering - delegates to base method
      return await this.getWorkflowSpotsByProgram(programId);
    } catch (error) {
      logger.error('Failed to get raw workflow spots', error, { programId });
      throw error;
    }
  }

  /**
   * Get all workflow spots across all programs
   * Returns all spots regardless of approval status, confidence level, or archived status
   *
   * Used for:
   * - Capacity approval workflow page (needs to show all spots for review)
   * - Administrative views requiring full visibility
   */
  async getAllWorkflowSpots(): Promise<WorkflowSpot[]> {
    try {
      logger.debug('Fetching all workflow spots');
      
      // Fetch all workflow spots without any filtering
      const allSpots = await this.firestoreService.list([
        orderBy('created_at', 'desc')
      ]);
      
      logger.debug(`Fetched ${allSpots.length} total workflow spots`);
      
      return allSpots;
    } catch (error) {
      console.error('❌ [WorkflowSpotService] Error getting all workflow spots:', error);
      logger.error('Failed to get all workflow spots', error);
      throw error;
    }
  }

  /**
   * Get all approved workflow spots across all programs
   * Business rules enforced:
   * - confidence_level > 5 (strictly greater than)
   * - is_archived === false (not archived)
   * - approval_status === 'approved' (approved spots only)
   *
   * Used for resourcing and cross-program views
   */
  async getAllApprovedWorkflowSpots(): Promise<WorkflowSpot[]> {
    try {
      
      // Fetch all workflow spots (no complex queries to avoid index requirements)
      const allSpots = await this.firestoreService.list([
        orderBy('created_at', 'desc')
      ]);
      
      
      // Filter client-side for approved spots with confidence > 5 and not archived
      const filteredSpots = allSpots.filter(spot =>
        spot.approval_status === 'approved' &&
        spot.confidence_level > 5 &&
        spot.is_archived === false
      );
      
      // Sort by confidence level (high to low)
      filteredSpots.sort((a, b) => b.confidence_level - a.confidence_level);
      
      return filteredSpots;
    } catch (error) {
      console.error('❌ [WorkflowSpotService] Error getting all approved workflow spots:', error);
      logger.error('Failed to get all approved workflow spots', error);
      throw error;
    }
  }

  /**
   * Update a workflow spot
   */
  async updateWorkflowSpot(
    spotId: string,
    updates: UpdateWorkflowSpotData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {

      // Validate input data
      const validatedUpdates = UpdateWorkflowSpotDataSchema.parse(updates);
      
      // Get current spot to increment version
      const currentSpot = await this.firestoreService.getRequired(spotId);
      
      // Create update data with proper typing
      const updateData: Partial<Omit<WorkflowSpot, 'id'>> = {
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      // Add validated updates to the update data
      if (validatedUpdates.workflow_name !== undefined) {
        updateData.workflow_name = validatedUpdates.workflow_name;
      }
      if (validatedUpdates.complexity !== undefined) {
        updateData.complexity = validatedUpdates.complexity;
      }
      if (validatedUpdates.team_experience !== undefined) {
        updateData.team_experience = validatedUpdates.team_experience;
      }
      if (validatedUpdates.project_style_preference !== undefined) {
        updateData.project_style_preference = validatedUpdates.project_style_preference;
      }
      if (validatedUpdates.confidence_level !== undefined) {
        updateData.confidence_level = validatedUpdates.confidence_level;

        // Append previous confidence value to history if it actually changed
        if (currentSpot.confidence_level !== validatedUpdates.confidence_level) {
          const spotRef = doc(db, COLLECTION_NAMES.WORKFLOW_SPOTS, spotId);
          await updateDoc(spotRef, {
            confidence_history: arrayUnion({
              previous_value: currentSpot.confidence_level,
              changed_at: Timestamp.now(),
              changed_by: userId,
            }),
          });
        }
      }
      if (validatedUpdates.decision_drivers !== undefined) {
        updateData.decision_drivers = validatedUpdates.decision_drivers;
      }
      if (validatedUpdates.first_responder !== undefined) {
        updateData.first_responder = validatedUpdates.first_responder;
      }
      if (validatedUpdates.production_support_1 !== undefined) {
        updateData.production_support_1 = validatedUpdates.production_support_1;
      }
      if (validatedUpdates.production_support_2 !== undefined) {
        updateData.production_support_2 = validatedUpdates.production_support_2;
      }
      if (validatedUpdates.strategy_pfm_support !== undefined) {
        updateData.strategy_pfm_support = validatedUpdates.strategy_pfm_support;
      }
      if (validatedUpdates.ideal_start_date !== undefined) {
        updateData.ideal_start_date = validatedUpdates.ideal_start_date;
      }
      if (validatedUpdates.metadata !== undefined) {
        updateData.metadata = validatedUpdates.metadata;
      }
      if (validatedUpdates.asana_task_gid !== undefined) {
        updateData.asana_task_gid = validatedUpdates.asana_task_gid;
      }
      if (validatedUpdates.earmarked_status !== undefined) {
        updateData.earmarked_status = validatedUpdates.earmarked_status;
      }
      if (validatedUpdates.earmarked_text !== undefined) {
        updateData.earmarked_text = validatedUpdates.earmarked_text;
      }
      if (validatedUpdates.notes !== undefined) {
        updateData.notes = {
          ...currentSpot.notes,
          content: validatedUpdates.notes,
          last_updated: Timestamp.now(),
          last_updated_by: userId,
          last_updated_by_name: userName,
        };
      }

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        changes: updates,
        cycleId: auditContext.cycleId,
      });

      
      // Emit activity event for workflow spot update
      try {
        // Build field changes array - track all metadata changes
        const fieldChanges = [];
        
        if (validatedUpdates.workflow_name !== undefined) {
          fieldChanges.push({
            field_name: 'workflow_name',
            old_value: currentSpot.workflow_name,
            new_value: validatedUpdates.workflow_name,
            display_name: 'Workflow Name'
          });
        }
        if (validatedUpdates.complexity !== undefined) {
          fieldChanges.push({
            field_name: 'complexity',
            old_value: currentSpot.complexity,
            new_value: validatedUpdates.complexity,
            display_name: 'Complexity'
          });
        }
        if (validatedUpdates.confidence_level !== undefined) {
          fieldChanges.push({
            field_name: 'confidence_level',
            old_value: currentSpot.confidence_level,
            new_value: validatedUpdates.confidence_level,
            display_name: 'Confidence Level'
          });
        }
        if (validatedUpdates.team_experience !== undefined) {
          fieldChanges.push({
            field_name: 'team_experience',
            old_value: currentSpot.team_experience.join(', '),
            new_value: validatedUpdates.team_experience.join(', '),
            display_name: 'Team Experience'
          });
        }
        if (validatedUpdates.project_style_preference !== undefined) {
          fieldChanges.push({
            field_name: 'project_style_preference',
            old_value: currentSpot.project_style_preference.join(', '),
            new_value: validatedUpdates.project_style_preference.join(', '),
            display_name: 'Project Style'
          });
        }
        if (validatedUpdates.decision_drivers !== undefined) {
          fieldChanges.push({
            field_name: 'decision_drivers',
            old_value: currentSpot.decision_drivers.join(', '),
            new_value: validatedUpdates.decision_drivers.join(', '),
            display_name: 'Decision Drivers'
          });
        }
        if (validatedUpdates.first_responder !== undefined) {
          fieldChanges.push({
            field_name: 'first_responder',
            old_value: currentSpot.first_responder,
            new_value: validatedUpdates.first_responder,
            display_name: 'First Responder'
          });
        }
        if (validatedUpdates.production_support_1 !== undefined) {
          fieldChanges.push({
            field_name: 'production_support_1',
            old_value: currentSpot.production_support_1,
            new_value: validatedUpdates.production_support_1,
            display_name: 'Production Support 1'
          });
        }
        if (validatedUpdates.production_support_2 !== undefined) {
          fieldChanges.push({
            field_name: 'production_support_2',
            old_value: currentSpot.production_support_2,
            new_value: validatedUpdates.production_support_2,
            display_name: 'Production Support 2'
          });
        }
        if (validatedUpdates.strategy_pfm_support !== undefined) {
          fieldChanges.push({
            field_name: 'strategy_pfm_support',
            old_value: currentSpot.strategy_pfm_support,
            new_value: validatedUpdates.strategy_pfm_support,
            display_name: 'Strategy/PFM Support'
          });
        }
        if (validatedUpdates.ideal_start_date !== undefined) {
          fieldChanges.push({
            field_name: 'ideal_start_date',
            old_value: currentSpot.ideal_start_date,
            new_value: validatedUpdates.ideal_start_date,
            display_name: 'Ideal Start Date'
          });
        }
        
        if (validatedUpdates.notes !== undefined) {
          fieldChanges.push({
            field_name: 'notes_content',
            old_value: currentSpot.notes.content,
            new_value: validatedUpdates.notes,
            display_name: 'Main Notes'
          });
        }

        if (fieldChanges.length === 0) return;

        await activityEventService.emitUpdate({
          domain: 'workflow',
          event_type: 'workflow_spot.updated',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'workflow_spot',
            resource_id: spotId,
            resource_name: currentSpot.workflow_name,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          fields_changed: fieldChanges,
          previous_state: Object.fromEntries(
            Object.entries({
              workflow_name: currentSpot.workflow_name,
              complexity: currentSpot.complexity,
              confidence_level: currentSpot.confidence_level,
              team_experience: currentSpot.team_experience,
              project_style_preference: currentSpot.project_style_preference,
              decision_drivers: currentSpot.decision_drivers,
              first_responder: currentSpot.first_responder,
              production_support_1: currentSpot.production_support_1,
              production_support_2: currentSpot.production_support_2,
              strategy_pfm_support: currentSpot.strategy_pfm_support,
              ideal_start_date: currentSpot.ideal_start_date,
            }).filter(([_, value]) => value !== undefined && value !== null)
          ),
          new_state: Object.fromEntries(
            Object.entries({
              workflow_name: validatedUpdates.workflow_name ?? currentSpot.workflow_name,
              complexity: validatedUpdates.complexity ?? currentSpot.complexity,
              confidence_level: validatedUpdates.confidence_level ?? currentSpot.confidence_level,
              team_experience: validatedUpdates.team_experience ?? currentSpot.team_experience,
              project_style_preference: validatedUpdates.project_style_preference ?? currentSpot.project_style_preference,
              decision_drivers: validatedUpdates.decision_drivers ?? currentSpot.decision_drivers,
              first_responder: validatedUpdates.first_responder ?? currentSpot.first_responder,
              production_support_1: validatedUpdates.production_support_1 ?? currentSpot.production_support_1,
              production_support_2: validatedUpdates.production_support_2 ?? currentSpot.production_support_2,
              strategy_pfm_support: validatedUpdates.strategy_pfm_support ?? currentSpot.strategy_pfm_support,
              ideal_start_date: validatedUpdates.ideal_start_date ?? currentSpot.ideal_start_date,
            }).filter(([_, value]) => value !== undefined && value !== null)
          ),
          metadata: {
            affected_user_ids: [userId],
            affected_workflow_ids: [],
            affected_employee_ids: [],
            tags: ['workflow', 'workflow_spot', 'updated', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit workflow_spot.updated event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to update workflow spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Delete a workflow spot
   */
  async deleteWorkflowSpot(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {

      await this.firestoreService.delete(spotId, {
        userId,
        userName,
        action: 'delete',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        cycleId: auditContext.cycleId,
      });

    } catch (error) {
      logger.error('Failed to delete workflow spot', error, { spotId });
      throw error;
    }
  }

  // ============================================================================
  // APPROVAL WORKFLOW METHODS
  // ============================================================================

  /**
   * Submit workflow spot for approval
   */
  async submitForApproval(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {

      const currentSpot = await this.firestoreService.getRequired(spotId);
      
      // Spots are now created with pending_approval status by default
      // This method is kept for potential future use or re-submission

      const now = Timestamp.now();
      
      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'submitted',
        actor_id: userId,
        actor_name: userName,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: 'Submitted for approval',
        timestamp: now,
        message_type: 'approval_request',
        metadata: {
          previous_status: currentSpot.approval_status,
          new_status: 'pending_approval',
        },
      };

      const updateData: Partial<WorkflowSpot> = {
        approval_status: 'pending_approval',
        approval_metadata: {
          ...currentSpot.approval_metadata,
          submitted_at: now,
          submitted_by: userId,
          submitted_by_name: userName,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        changes: { approval_status: 'pending_approval' },
        cycleId: auditContext.cycleId,
      });

    } catch (error) {
      logger.error('Failed to submit workflow spot for approval', error, { spotId });
      throw error;
    }
  }

  /**
   * Approve workflow spot
   */
  async approveWorkflowSpot(
    spotId: string,
    reviewNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Approving workflow spot', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);
      
      // Allow approving spots with 'pending_approval' or legacy 'draft' status
      // @ts-ignore - Temporarily allow 'draft' for backward compatibility with existing data
      if (currentSpot.approval_status !== 'pending_approval' && currentSpot.approval_status !== 'draft') {
        throw new Error(`Cannot approve workflow spot with status: ${currentSpot.approval_status}`);
      }

      const now = Timestamp.now();
      
      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'approved',
        actor_id: userId,
        actor_name: userName,
        notes: reviewNotes,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Approved: ${reviewNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {
          previous_status: currentSpot.approval_status,
          new_status: 'approved',
        },
      };

      const updateData: Partial<WorkflowSpot> = {
        approval_status: 'approved',
        approval_metadata: {
          ...currentSpot.approval_metadata,
          reviewed_at: now,
          reviewed_by: userId,
          reviewed_by_name: userName,
          review_notes: reviewNotes,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        changes: { approval_status: 'approved' },
        cycleId: auditContext.cycleId,
      });
      
      // Emit activity event for workflow spot approval
      try {
        // Build rich metadata in fields_changed similar to workflow_spot.created
        const fieldsChanged = [
          {
            field_name: 'approval_status',
            old_value: currentSpot.approval_status,
            new_value: 'approved',
            display_name: 'Approval Status'
          },
          {
            field_name: 'complexity',
            old_value: null,
            new_value: currentSpot.complexity,
            display_name: 'Complexity Level'
          },
          {
            field_name: 'confidence_level',
            old_value: null,
            new_value: currentSpot.confidence_level,
            display_name: 'Confidence Level'
          },
          {
            field_name: 'team_experience',
            old_value: null,
            new_value: currentSpot.team_experience.join(', '),
            display_name: 'Team Experience'
          },
          {
            field_name: 'project_style_preference',
            old_value: null,
            new_value: currentSpot.project_style_preference.join(', '),
            display_name: 'Project Style'
          },
          {
            field_name: 'decision_drivers',
            old_value: null,
            new_value: currentSpot.decision_drivers.join(', '),
            display_name: 'Decision Drivers'
          }
        ];
        
        // Add ideal_start_date if present
        if (currentSpot.ideal_start_date) {
          fieldsChanged.push({
            field_name: 'ideal_start_date',
            old_value: null,
            new_value: currentSpot.ideal_start_date,
            display_name: 'Ideal Start Date'
          });
        }
        
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'workflow_spot.approved',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'workflow_spot',
            resource_id: spotId,
            resource_name: currentSpot.workflow_name,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'approved',
            fields_changed: fieldsChanged,
            previous_state: {
              approval_status: currentSpot.approval_status,
            },
            new_state: Object.fromEntries(
              Object.entries({
                approval_status: 'approved',
                review_notes: reviewNotes,
                reviewed_by: userName,
                complexity: currentSpot.complexity,
                confidence_level: currentSpot.confidence_level,
                team_experience: currentSpot.team_experience,
                project_style_preference: currentSpot.project_style_preference,
                decision_drivers: currentSpot.decision_drivers,
                ideal_start_date: currentSpot.ideal_start_date,
              }).filter(([_, value]) => value !== undefined && value !== null)
            ),
          },
          message: `Approved workflow spot '${currentSpot.workflow_name}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: [userId, currentSpot.created_by],
            affected_workflow_ids: [],
            affected_employee_ids: [],
            tags: [
              'workflow',
              'workflow_spot',
              'approved',
              'status_change',
              'capacity',
              currentSpot.complexity,
              ...currentSpot.team_experience,
              ...currentSpot.project_style_preference
            ],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit workflow_spot.approved event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to approve workflow spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Archive workflow spot (hide from active view)
   */
  async archiveWorkflowSpot(
    spotId: string,
    archiveNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {

      const currentSpot = await this.firestoreService.getRequired(spotId);

      const now = Timestamp.now();
      
      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'archived',
        actor_id: userId,
        actor_name: userName,
        notes: archiveNotes,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Archived: ${archiveNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<WorkflowSpot> = {
        is_archived: true,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          reviewed_at: now,
          reviewed_by: userId,
          reviewed_by_name: userName,
          review_notes: archiveNotes,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        changes: { is_archived: true },
        cycleId: auditContext.cycleId,
      });

      
      // Emit activity event for workflow spot archival
      try {
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'workflow_spot.archived',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'workflow_spot',
            resource_id: spotId,
            resource_name: currentSpot.workflow_name,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'archived',
            fields_changed: [
              {
                field_name: 'is_archived',
                old_value: false,
                new_value: true,
                display_name: 'Archived Status'
              }
            ],
            previous_state: {
              is_archived: currentSpot.is_archived,
              approval_status: currentSpot.approval_status,
            },
            new_state: Object.fromEntries(
              Object.entries({
                is_archived: true,
                archive_notes: archiveNotes,
                archived_by: userName,
              }).filter(([_, value]) => value !== undefined && value !== null)
            ),
          },
          message: `Archived workflow spot '${currentSpot.workflow_name}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: [userId, currentSpot.created_by],
            affected_workflow_ids: [],
            affected_employee_ids: [],
            tags: ['workflow', 'workflow_spot', 'archived', 'status_change', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit workflow_spot.archived event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to archive workflow spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Unarchive workflow spot (restore to active view)
   */
  async unarchiveWorkflowSpot(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {

      const currentSpot = await this.firestoreService.getRequired(spotId);

      const now = Timestamp.now();
      
      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'unarchived',
        actor_id: userId,
        actor_name: userName,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: 'Unarchived',
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<WorkflowSpot> = {
        is_archived: false,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        changes: { is_archived: false },
        cycleId: auditContext.cycleId,
      });

    } catch (error) {
      logger.error('Failed to unarchive workflow spot', error, { spotId });
      throw error;
    }
  }
  /**
   * Revert workflow spot to pending approval status
   * Only allowed when status is 'approved'
   */
  async revertWorkflowSpotStatus(
    spotId: string,
    revertNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Reverting workflow spot status', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);
      
      // Only allow revert from approved status
      if (currentSpot.approval_status !== 'approved') {
        throw new Error(`Cannot revert workflow spot with status: ${currentSpot.approval_status}. Must be approved.`);
      }

      const now = Timestamp.now();
      const previousStatus: ApprovalStatus = 'pending_approval';
      
      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'submitted', // Using 'submitted' to track the revert action
        actor_id: userId,
        actor_name: userName,
        notes: `Reverted from ${currentSpot.approval_status} to ${previousStatus}: ${revertNotes}`,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Status reverted from ${currentSpot.approval_status} to ${previousStatus}: ${revertNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {
          previous_status: currentSpot.approval_status,
          new_status: previousStatus,
        },
      };

      const updateData: Partial<WorkflowSpot> = {
        approval_status: previousStatus,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        changes: { approval_status: previousStatus },
        cycleId: auditContext.cycleId,
      });

      logger.info('Workflow spot status reverted successfully', { spotId, previousStatus });
      
      // Emit activity event for workflow spot revert
      try {
        const fieldsChanged = [
          {
            field_name: 'approval_status',
            old_value: currentSpot.approval_status,
            new_value: previousStatus,
            display_name: 'Approval Status'
          }
        ];
        
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'workflow_spot.updated',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'workflow_spot',
            resource_id: spotId,
            resource_name: currentSpot.workflow_name,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'updated',
            fields_changed: fieldsChanged,
            previous_state: {
              approval_status: currentSpot.approval_status,
            },
            new_state: {
              approval_status: previousStatus,
              revert_notes: revertNotes,
              reverted_by: userName,
            },
          },
          message: `Reverted workflow spot '${currentSpot.workflow_name}' status${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: [userId, currentSpot.created_by],
            affected_workflow_ids: [],
            affected_employee_ids: [],
            tags: ['workflow', 'workflow_spot', 'updated', 'revert', 'status_change', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit workflow_spot.reverted event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to revert workflow spot status', error, { spotId });
      throw error;
    }
  }


  // ============================================================================
  // CONVERSATION MANAGEMENT
  // ============================================================================

  /**
   * Add conversation entry to workflow spot
   */
  async addConversationEntry(
    spotId: string,
    entry: AddConversationEntryData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Adding conversation entry to workflow spot', { spotId });

      // Validate input data
      const validatedEntry = AddConversationEntryDataSchema.parse(entry);
      
      const currentSpot = await this.firestoreService.getRequired(spotId);
      const now = Timestamp.now();
      
      // Create conversation entry - only include metadata if it exists
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: validatedEntry.message,
        timestamp: now,
        message_type: validatedEntry.message_type,
        ...(validatedEntry.metadata && { metadata: validatedEntry.metadata }),
      };

      const updateData: Partial<WorkflowSpot> = {
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'workflow_spot',
        resourceId: spotId,
        changes: { conversation_entry_added: true },
        cycleId: auditContext.cycleId,
      });

      logger.info('Conversation entry added successfully', { spotId });
      
      // Emit activity event for comment
      try {
        // Extract mentioned users from comment text (e.g., @username or @user.name)
        const mentionRegex = /@(\w+(?:\.\w+)?)/g;
        const mentions = [...validatedEntry.message.matchAll(mentionRegex)];
        const mentionedUserIds = mentions.map(match => match[1]).filter(Boolean);
        
        // Build affected user IDs - include commenter and any mentioned users
        const affectedUserIds = [userId, ...mentionedUserIds];
        
        // Create message preview (first 200 chars)
        const messagePreview = validatedEntry.message.length > 200
          ? validatedEntry.message.substring(0, 200) + '...'
          : validatedEntry.message;
        
        // Determine visibility based on context
        // If team context is available, use 'team', otherwise use 'program'
        const visibility = currentSpot.team_id ? 'team' : 'program';
        
        // Build fields_changed array with rich metadata
        const fieldsChanged = [
          {
            field_name: 'comment_text',
            old_value: null,
            new_value: messagePreview,
            display_name: 'Comment'
          },
          {
            field_name: 'comment_type',
            old_value: null,
            new_value: validatedEntry.message_type,
            display_name: 'Comment Type'
          }
        ];
        
        // Add mentioned users if any
        if (mentionedUserIds.length > 0) {
          fieldsChanged.push({
            field_name: 'mentioned_users',
            old_value: null,
            new_value: mentionedUserIds.join(', '),
            display_name: 'Mentioned Users'
          });
        }
        
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'workflow_spot.commented',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'workflow_spot',
            resource_id: spotId,
            resource_name: currentSpot.workflow_name,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'submitted',
            fields_changed: fieldsChanged,
            new_state: Object.fromEntries(
              Object.entries({
                workflow_spot_id: spotId,
                workflow_spot_name: currentSpot.workflow_name,
                comment_type: validatedEntry.message_type,
              }).filter(([_, value]) => value !== undefined && value !== null)
            ),
          },
          message: `Commented on workflow spot '${currentSpot.workflow_name}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: affectedUserIds,
            affected_workflow_ids: [spotId],
            affected_employee_ids: [],
            tags: [
              'workflow',
              'workflow_spot',
              'commented',
              'conversation',
              'capacity',
              validatedEntry.message_type,
              ...(mentionedUserIds.length > 0 ? ['mentions'] : [])
            ],
            visibility,
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit workflow_spot.commented event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to add conversation entry', error, { spotId });
      throw error;
    }
  }

}

// Export singleton instance
export const workflowSpotService = new WorkflowSpotService();

// Class kept for internal use; singleton instance is the public API

// Export types for external use (remove duplicate export)